import { FunctionComponent, PropsWithChildren, createContext, useCallback, useContext, useMemo, useRef } from 'react';

import { RecordInteractionRequest, UserLikePostStatusRequest } from '@vestico/model';

import { WidgetAuthContext } from './MainContext';
import { ConfigContext } from './WidgetEventsManager';
import { InteractionsApiClient } from './services/apiClientInteractions';

export interface InteractionContextProps {
  recordInteraction: (request: RecordInteractionRequest) => Promise<boolean>;
  getUserLikedPost: (request: UserLikePostStatusRequest) => Promise<boolean>;
}

export const UserInteractionsContext = createContext<InteractionContextProps>({} as InteractionContextProps);

export const UserInteractionsProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { isPreview, clientId, iOSBundleIdentifier, sdkAPIKey } = useContext(ConfigContext);
  const interactionsAPI = useRef(new InteractionsApiClient(clientId, isPreview, sdkAPIKey, iOSBundleIdentifier));
  const { authInfo, resetAuth } = useContext(WidgetAuthContext);

  const recordInteraction = useCallback(
    async (request: RecordInteractionRequest) => {
      const success = await interactionsAPI.current.recordInteraction(request, { authInfo, resetAuth });
      return success;
    },
    [interactionsAPI, authInfo, resetAuth]
  );

  const getUserLikedPost = useCallback(
    async (request: UserLikePostStatusRequest) => {
      const likesPost = await interactionsAPI.current.getUserLikedPost(request, { authInfo, resetAuth });
      return likesPost;
    },
    [authInfo, resetAuth]
  );

  const contextValue = useMemo(() => {
    return { recordInteraction, getUserLikedPost };
  }, [recordInteraction, getUserLikedPost]);

  return <UserInteractionsContext.Provider value={contextValue}>{children}</UserInteractionsContext.Provider>;
};
